import { useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { ref, onValue, off } from 'firebase/database';
import { auth, database } from '../firebase';

export const login = (email, password) => {
  return signInWithEmailAndPassword(auth, email, password);
};

export const logout = () => {
  return signOut(auth);
};

export const useFirebaseAuth = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let userDbListener = () => {}; // Initial no-op cleanup function

    const authUnsubscribe = onAuthStateChanged(auth, (user) => {
      // First, clean up any existing database listener from a previous user
      userDbListener();

      if (user) {
        // User is authenticated. Set them synchronously.
        setCurrentUser(user);
        // Keep loading true until we get their data from the DB.
        setLoading(true); 

        const userRef = ref(database, `users/${user.uid}`);
        const listener = onValue(userRef, (snapshot) => {
          const dbUserData = snapshot.val();
          if (dbUserData) {
            if (dbUserData.disabled) {
              signOut(auth);
              setError('Your account has been blocked. Please contact support.');
              setUserData(null);
              setLoading(false);
            } else {
              setUserData(dbUserData);
              setError(null);
            }
          } else {
            setUserData(null); // Clear previous user data if any
            setError('Your user account is not configured in the database.');
          }
          // Now that we have the user data (or lack thereof), we're done loading.
          setLoading(false);
        }, (dbError) => {
          console.error("Firebase user read failed: ", dbError);
          setUserData(null);
          setError('Failed to retrieve user data.');
          setLoading(false);
        });

        // Assign the cleanup function for this specific listener
        userDbListener = () => off(userRef, 'value', listener);
      } else {
        // User is not authenticated.
        setCurrentUser(null);
        setUserData(null);
        setLoading(false);
      }
    });

    // Cleanup function for the component unmount.
    return () => {
      authUnsubscribe(); // Unsubscribe from auth state changes
      userDbListener();  // Unsubscribe from any active DB listener
    };
  }, []);

  return { currentUser, userData, loading, error };
};
